import { getVisibleChildren, sumWidthOfChildrenWithGaps } from './utilities';
import { DEFAULT_DRAG_SNAP_THRESHOLD, SLIDE_GAP } from './constants';

const clampIndex = (index, maxIndex) => {
  if (maxIndex < 0) return 0;
  return Math.max(0, Math.min(index, maxIndex));
};

/**
 * @typedef {Object} SliderState
 * @property {number} index - The current active slide index.
 * @property {number} offset - The current offset of the slider track in pixels.
 * @property {Object} slides - The stored slides.
 */

/**
 * Generates the default state object for a slider component.
 *
 * @param {Array} slides - An array of slide elements to be used in the slider.
 * @returns {SliderState} The default state object for the slider.
 * @returns {Array} return.slides - The array of slides provided as input.
 * @returns {number} return.index - The current index of the active slide, initialized to 0.
 * @returns {number} return.offset - The current offset of the slider track in pixels,
 * initialized to 0.
 */
export const getDefaultState = (slides) => ({
  slides, index: 0, offset: 0,
});

/**
 * Ensure the offset does not exceed the visible portion of the container.
 * This prevents showing blank space at either end of the slider.
 *
 * @param {number} offset - The calculated new offset.
 * @param {number} containerWidth - The width of the container.
 * @param {number} trackWidth - The width of the track.
 * @returns {number} The adjusted offset within the visible bounds.
 */
export const setLimitsOnOffset = (offset, containerWidth, trackWidth) => {
  const visibleGap = containerWidth - trackWidth;
  const limitedOffset = Math.min(0, Math.max(offset, visibleGap));
  return limitedOffset;
};

/**
 * Calculate the offset required to correctly position the slider track based on the new index.
 *
 * @param {SliderState} state - The current state of the slider.
 * @param {number} index - The new index of the slide to move to.
 * @param {HTMLElement} container - The container element of the slider.
 * @param {HTMLElement} track - The track element that contains the slides.
 * @returns {number} The calculated offset in pixels.
 */
function calculateOffset(
  index,
  container,
  track,
) {
  const containerWidth = container?.offsetWidth;
  const trackWidth = track?.offsetWidth;
  const offsetLimit = containerWidth - trackWidth;
  const children = Array.from(track?.children);
  const offsetChildren = children.slice(0, index);
  const offsetWidth = sumWidthOfChildrenWithGaps(offsetChildren, SLIDE_GAP);

  const offset = setLimitsOnOffset(
    -offsetWidth,
    containerWidth,
    trackWidth,
  );

  return {
    offset,
    isLastSlide: Math.abs(offset) >= Math.abs(offsetLimit),
  };
}

/**
 * Helper function to calculate the new index and offset when transitioning to a new slide.
 *
 * @param {SliderState} state - The current state of the slider.
 * @param {Function} indexHandler - The function to calculate the new index (left or right).
 * @param {HTMLElement} container - The container element of the slider.
 * @param {HTMLElement} track - The track element that contains the slides.
 * @returns {Object} An object containing the new index and offset.
 */
export function transitionToSlide(
  state,
  indexHandler,
  container,
  track,
) {
  const index = indexHandler(state);
  const { offset, isLastSlide } = calculateOffset(index, container, track);
  return {
    index, offset, isLastSlide,
  };
}

/**
 * Determine the next slide to show after a drag action based on the drag delta.
 * Enhanced to handle mobile edge cases and prevent blank space.
 *
 * @param {SliderState} state - The current state of the slider.
 * @param {number} delta - The amount of drag movement in pixels.
 * @param {HTMLElement} container - The container element of the slider.
 * @param {HTMLElement} track - The track element that contains the slides.
 * @param {boolean} isInfinite - Flag to determine if the slider is infinite.
 * @param {boolean} isTouchEvent - Flag to determine if this is from a touch event.
 * @param {number} slidesToShow - Number of slides to show at once, if specified.
 * @returns {SliderState} The new state of the slider after determining the appropriate slide.
 */
export function determineNextSlideAfterDrag(
  state,
  delta,
  container,
  track,
  isInfinite,
  isTouchEvent,
  slidesToShow,
) {
  const useInfinite = isInfinite === undefined ? true : isInfinite;
  const useTouchEvent = isTouchEvent === undefined ? false : isTouchEvent;
  const { offset } = state;
  const children = Array.from(track.children);

  if (Math.abs(delta) < DEFAULT_DRAG_SNAP_THRESHOLD) return state;

  const direction = Math.sign(delta);

  const containerWidth = container.offsetWidth;
  const trackWidth = track.scrollWidth;

  if (useTouchEvent && !useInfinite) {
    if (direction > 0 && state.index === 0) {
      return state;
    }

    if (direction < 0 && state.index >= state.slides.length - 1) {
      return state;
    }
  }

  if (direction < 0 && offset <= (-trackWidth + containerWidth)) {
    if (useInfinite) {
      return transitionToSlide(state, () => 0, container, track, true);
    }
    return state;
  }

  if (direction > 0 && offset === 0) {
    if (useInfinite) {
      return transitionToSlide(state, () => children.length - 1, container, track, true);
    }
    return state;
  }

  const visibleChildren = getVisibleChildren(container, track);
  let nextIndex;
  if (slidesToShow && slidesToShow > 0) {
    const maxIndex = Math.max(0, state.slides.length - slidesToShow);
    nextIndex = direction > 0
      ? Math.max(state.index - 1, 0)
      : Math.min(state.index + 1, maxIndex);
  } else if (useTouchEvent && !useInfinite) {
    nextIndex = direction > 0
      ? Math.max(state.index - 1, 0)
      : Math.min(state.index + 1, state.slides.length - 1);
  } else {
    const firstChildIndex = children.indexOf(visibleChildren[0]);
    const nextChildIndex = visibleChildren[1] ? children.indexOf(visibleChildren[1]) : -1;

    if (direction > 0) {
      nextIndex = Math.min(firstChildIndex, state.slides.length - 1);
    } else {
      nextIndex = nextChildIndex !== -1 ? nextChildIndex : state.index + 1;
    }
  }

  nextIndex = Math.max(0, Math.min(nextIndex, state.slides.length - 1));
  return transitionToSlide(state, () => nextIndex, container, track, true);
}

/**
 * Reducer function to manage the state of the slider track.
 *
 * @param {SliderState} state - The current state of the slider.
 * @param {Object} action - The action to be performed.
 * @returns {SliderState} The new state of the slider.
 */
export default function slideTrackReducer(state, action) {
  const {
    container,
    track,
    isInfinite = true,
    slidesToShow,
  } = action;
  let actionState = {};

  switch (action.type) {
    case 'containerResize':
      actionState = {
        offset: calculateOffset(
          state.index,
          container,
          track,
        ).offset,
        isLastSlide: calculateOffset(
          state.index,
          container,
          track,
        ).isLastSlide,
      };
      break;
    case 'setIndex': {
      const maxIndex = state.slides.length - 1;
      const validIndex = clampIndex(action.index, maxIndex);

      actionState = {
        index: validIndex,
        offset: calculateOffset(
          validIndex,
          container,
          track,
        ).offset,
        isLastSlide: calculateOffset(
          validIndex,
          container,
          track,
        ).isLastSlide,
      };
      break;
    }
    case 'slideLeft': {
      const slideStep = slidesToShow ? 1 : 1;
      const maxIndex = state.slides.length - 1;

      actionState = {
        ...transitionToSlide(
          state,
          (currentState) => {
            const newIndex = currentState.index - slideStep;
            if (newIndex < 0) {
              return isInfinite ? maxIndex : 0;
            }
            return clampIndex(newIndex, maxIndex);
          },
          container,
          track,
        ),
      };
      break;
    }
    case 'slideRight': {
      const slideStep = slidesToShow ? 1 : 1;
      const maxIndex = slidesToShow
        ? state.slides.length - slidesToShow
        : state.slides.length - 1;

      actionState = {
        ...transitionToSlide(
          state,
          (currentState) => {
            const newIndex = currentState.index + slideStep;
            if (newIndex > maxIndex) {
              return isInfinite ? 0 : maxIndex;
            }
            return clampIndex(newIndex, maxIndex);
          },
          container,
          track,
        ),
      };
      break;
    }
    case 'slidesUpdated':
      actionState = {
        slides: action.slides,
      };
      break;
    case 'determineNextSlideAfterDrag':
      actionState = {
        ...determineNextSlideAfterDrag(
          state,
          action.delta,
          container,
          track,
          isInfinite,
          action.isTouchEvent,
          slidesToShow,
        ),
      };
      break;
    default: break;
  }

  return { ...state, ...actionState };
}
